export type CompetitionsJsonInput = Record<string, CompetitionEntry>;

export type CompetitionEntry = {
  name: string;
  abbreviation: string;
  sport: string;
  alternateNames?: string[];
  location?: Record<string, unknown>;
  metadata?: {
    colors?: Record<string, unknown>;
    images?: {
      primary?: { name: string; type: string; ext: string; folder: string };
      location?: Record<string, unknown>;
      backgrounds?: Array<Record<string, unknown>>;
      associations?: Array<{ name: string; type: string; ext: string; folder: string }>;
    };
  };
  cid?: string;
};

export type CompetitionsMap = Record<string, CompetitionEntry>;

/**
 * Parses raw competitions.json into a lookup map with multiple alias keys.
 * Keys: uuid, abbreviation, name, sport-abbreviation, alternateNames, icon names, association names.
 * Used by both StyleDictionary (build-time) and client (runtime).
 */
export function parseCompetitionsData(input: CompetitionsJsonInput): CompetitionsMap {
  const map: CompetitionsMap = {};
  if (!input || typeof input !== "object") return map;

  for (const [id, comp] of Object.entries(input)) {
    if (!comp?.name || !comp?.abbreviation) continue;

    // UUID key
    map[id] = comp;

    // Collect unique name keys
    const sportAbbr = comp.sport && comp.abbreviation
      ? `${comp.sport}-${comp.abbreviation}`.toLowerCase()
      : "";
    const names = new Set([
      comp.abbreviation.toLowerCase(),
      comp.name.toLowerCase(),
      ...(sportAbbr ? [sportAbbr] : []),
      ...(comp.alternateNames || []).map(n => n.toLowerCase()),
    ]);
    for (const name of Array.from(names)) map[name] = comp;

    // Image keys
    const primaryIcon = comp.metadata?.images?.primary?.name;
    if (primaryIcon) map[primaryIcon] = comp;
    for (const assoc of comp.metadata?.images?.associations || []) {
      if (assoc.name) map[assoc.name] = comp;
    }
  }

  return map;
}
